import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { EChartsOption } from 'echarts';
import { CustomChart, LineChart } from 'echarts/charts';
import { GridComponent, MarkLineComponent, MarkAreaComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import { MEASURE_COLORS } from '../measures/measures.model';
import { type HistogramOverlay } from '../measures/measures-highlight.util';
import { buildOverlaySeries, fmt, type RenderApi } from '../chart-shared';
import {
  DEFAULT_DEVIATION_PLOT_DISPLAY,
  type DeviationPlotDisplayConfig,
  type DeviationPlotParams,
} from './deviation-plot-chart.model';

echarts.use([
  CustomChart,
  LineChart,
  CanvasRenderer,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkAreaComponent,
]);

const COLOR_MEAN = MEASURE_COLORS['mean']; // rose-500 #f43f5e

@Component({
  selector: 'dojo-deviation-plot-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './deviation-plot-chart.component.html',
  styleUrl: './deviation-plot-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full h-full' },
})
export class DeviationPlotChartComponent {
  readonly params = input.required<DeviationPlotParams>();
  readonly display = input<DeviationPlotDisplayConfig>({});
  readonly overlay = input<HistogramOverlay>({ markLines: [], markAreas: [] });

  readonly chartOptions = computed<EChartsOption>(() => {
    const data = this.params().data;
    const display = { ...DEFAULT_DEVIATION_PLOT_DISPLAY, ...this.display() };
    const overlay = this.overlay();

    if (data.length === 0) {
      return buildEmptyChart();
    }

    const mean = data.reduce((sum, v) => sum + v, 0) / data.length;
    const dataMin = Math.min(...data);
    const dataMax = Math.max(...data);
    const range = dataMax - dataMin || 1;
    const yPadding = range * 0.15;
    const yMin = dataMin - yPadding;
    const yMax = dataMax + yPadding;

    // Line series — connects data points by observation index
    const lineData = data.map((v, i) => [i + 1, v]);

    // Custom series — vertical deviation segments
    const deviationData = data.map((v, i) => [i + 1, v, mean]);

    const series: NonNullable<EChartsOption['series']> = [
      buildDeviationSeries(deviationData, display),
      buildLineSeries(lineData, display, mean),
      buildOverlaySeries(overlay, 'y'),
    ];

    return {
      tooltip: {
        show: true,
        trigger: 'axis',
        confine: true,
        backgroundColor: '#fff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        extraCssText: 'box-shadow: 0 2px 8px rgba(0,0,0,0.08);',
        padding: [10, 14],
        textStyle: { fontSize: 12, color: '#374151' },
        axisPointer: { type: 'line', lineStyle: { color: '#d1d5db', type: 'dashed' } },
        formatter: (params: unknown) => {
          const items = params as Array<{ seriesName?: string; data?: number[] }>;
          const lineItem = items.find((p) => p.seriesName === 'Values' && Array.isArray(p.data));
          if (!lineItem?.data) {
            return '';
          }
          const observation = lineItem.data[0];
          const value = lineItem.data[1];
          const deviation = value - mean;
          const sign = deviation >= 0 ? '+' : '';
          const devColor = deviation >= 0 ? display.positiveColor ?? '#6366f1' : display.negativeColor ?? '#f43f5e';
          return [
            `<div style="margin-bottom:4px;font-weight:600;color:#374151">Observation ${observation}</div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#6b7280">Value</span><span style="font-weight:600">${fmt(
              value,
            )}</span></div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#6b7280">Mean</span><span style="font-weight:600">${fmt(
              mean,
            )}</span></div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#6b7280">Deviation</span><span style="font-weight:600;color:${devColor}">${sign}${fmt(
              deviation,
            )}</span></div>`,
          ].join('');
        },
      },
      grid: {
        top: 30,
        bottom: 30,
        left: 20,
        right: 20,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'Observation',
        nameLocation: 'middle',
        nameGap: 25,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        min: 0.5,
        max: data.length + 0.5,
        interval: 1,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        splitLine: { show: false },
        axisLabel: {
          fontSize: 12,
          color: '#6b7280',
          formatter: (v: number) => (Number.isInteger(v) ? String(v) : ''),
        },
      },
      yAxis: {
        type: 'value',
        name: 'Value',
        nameLocation: 'end',
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        min: yMin,
        max: yMax,
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { fontSize: 12, color: '#6b7280' },
      },
      series,
    };
  });
}

// ---------------------------------------------------------------------------
// Series builders
// ---------------------------------------------------------------------------

/**
 * Custom series: vertical deviation segments from mean to each data point.
 * Each item in data: [observation, value, mean]
 */
function buildDeviationSeries(data: number[][], display: DeviationPlotDisplayConfig): Record<string, unknown> {
  const positiveColor = display.positiveColor ?? '#6366f1';
  const negativeColor = display.negativeColor ?? '#f43f5e';

  return {
    type: 'custom',
    name: 'Deviation',
    data,
    z: 1,
    silent: true,
    renderItem: (_params: unknown, api: RenderApi) => {
      const x = api.value(0);
      const value = api.value(1);
      const mean = api.value(2);

      const topPt = api.coord([x, value]);
      const bottomPt = api.coord([x, mean]);
      const color = value >= mean ? positiveColor : negativeColor;

      return {
        type: 'line',
        shape: {
          x1: topPt[0],
          y1: topPt[1],
          x2: bottomPt[0],
          y2: bottomPt[1],
        },
        style: {
          stroke: color,
          lineWidth: 2,
        },
      };
    },
    tooltip: { show: false },
  };
}

/**
 * Line series: connects all sorted data points with circle markers.
 * Also carries the mean markLine.
 */
function buildLineSeries(data: number[][], display: DeviationPlotDisplayConfig, mean: number): Record<string, unknown> {
  const lineColor = display.lineColor ?? '#6366f1';

  return {
    type: 'line',
    name: 'Values',
    data,
    z: 3,
    symbol: 'circle',
    symbolSize: 8,
    lineStyle: {
      color: lineColor,
      width: 2,
    },
    itemStyle: {
      color: lineColor,
      borderColor: '#fff',
      borderWidth: 1.5,
    },
    markLine: {
      symbol: 'none',
      animation: false,
      silent: true,
      data: [
        {
          yAxis: mean,
          label: {
            formatter: `x̄ = ${fmt(mean)}`,
            position: 'insideEndTop',
            fontSize: 11,
            fontWeight: 500,
            color: COLOR_MEAN,
          },
          lineStyle: {
            color: COLOR_MEAN,
            type: 'solid',
            width: 2,
          },
        },
      ],
    },
  };
}

// ---------------------------------------------------------------------------
// Empty chart fallback
// ---------------------------------------------------------------------------

function buildEmptyChart(): EChartsOption {
  return {
    grid: { top: 30, bottom: 30, left: 20, right: 20, containLabel: true },
    xAxis: {
      type: 'value',
      name: 'Observation',
      nameLocation: 'middle',
      nameGap: 25,
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      axisLine: { lineStyle: { color: '#d1d5db' } },
      splitLine: { show: false },
      axisLabel: { fontSize: 12, color: '#6b7280' },
    },
    yAxis: {
      type: 'value',
      name: 'Value',
      nameLocation: 'end',
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      axisLine: { lineStyle: { color: '#d1d5db' } },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
      axisLabel: { fontSize: 12, color: '#6b7280' },
    },
    series: [],
  };
}
